import React, { useEffect, useState } from 'react';
import { paymentsApi, subscriptionsApi } from '../lib/api';
import { CreditCard, Search, CheckCircle, XCircle, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const statusConfig = {
  confirmed: { label: 'Подтвержден', class: 'badge-active', icon: CheckCircle },
  pending: { label: 'Ожидает', class: 'badge-pending', icon: Clock },
  cancelled: { label: 'Отменен', class: 'badge-cancelled', icon: XCircle },
  expired: { label: 'Истек', class: 'badge-expired', icon: AlertCircle },
};

const methodLabels = {
  platega: 'Platega.io',
  crypto: 'Криптовалюта',
  manual: 'Ручной платеж',
};

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchPayments = async () => {
    try {
      const res = await paymentsApi.getAll({ limit: 500 });
      setPayments(res.data);
    } catch (error) {
      toast.error('Ошибка загрузки платежей');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const handleConfirm = async (payment) => {
    if (!window.confirm('Подтвердить платеж? Подписка будет активирована.')) return;

    try {
      await paymentsApi.confirm(payment.id);
      toast.success('Платеж подтвержден, подписка активирована');
      fetchPayments();
    } catch (error) {
      toast.error('Ошибка подтверждения платежа');
    }
  };

  const handleCancel = async (payment) => {
    if (!window.confirm('Отменить платеж?')) return;

    try {
      await paymentsApi.cancel(payment.id);
      toast.success('Платеж отменен');
      fetchPayments();
    } catch (error) {
      toast.error('Ошибка отмены платежа');
    }
  };

  const filteredPayments = payments.filter(payment => {
    if (filterStatus !== 'all' && payment.status !== filterStatus) return false;
    if (filterMethod !== 'all' && payment.payment_method !== filterMethod) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        payment.user_telegram_id.toLowerCase().includes(query) ||
        payment.id.toLowerCase().includes(query) ||
        (payment.external_id && payment.external_id.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="payments-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Платежи</h1>
        <p className="text-zinc-500">Журнал транзакций и ручное подтверждение платежей</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Поиск по ID платежа или пользователю..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10"
            data-testid="search-payments"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-[#121212] border-white/10">
            <SelectValue placeholder="Статус" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="confirmed">Подтвержденные</SelectItem>
            <SelectItem value="pending">Ожидающие</SelectItem>
            <SelectItem value="cancelled">Отмененные</SelectItem>
            <SelectItem value="expired">Истекшие</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterMethod} onValueChange={setFilterMethod}>
          <SelectTrigger className="w-48 bg-[#121212] border-white/10">
            <SelectValue placeholder="Способ оплаты" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все способы</SelectItem>
            <SelectItem value="platega">Platega.io</SelectItem>
            <SelectItem value="crypto">Криптовалюта</SelectItem>
            <SelectItem value="manual">Ручной платеж</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Payments Table */}
      {filteredPayments.length > 0 ? (
        <div className="card overflow-x-auto">
          <table className="data-table data-table-responsive">
            <thead>
              <tr>
                <th>ID платежа</th>
                <th>Пользователь</th>
                <th>Сумма</th>
                <th>Способ оплаты</th>
                <th>Статус</th>
                <th>Дата</th>
                <th className="text-right">Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => {
                const status = statusConfig[payment.status];
                const StatusIcon = status.icon;
                return (
                  <tr key={payment.id} data-testid={`payment-row-${payment.id}`}>
                    <td data-label="ID платежа">
                      <code className="text-xs bg-white/5 px-2 py-1 rounded font-mono text-emerald-400">
                        {payment.id.slice(0, 8)}...
                      </code>
                    </td>
                    <td data-label="Пользователь">
                      <p className="font-mono text-sm text-zinc-300">{payment.user_telegram_id}</p>
                    </td>
                    <td data-label="Сумма">
                      <span className="text-white font-medium">
                        {payment.amount} {payment.currency}
                      </span>
                    </td>
                    <td data-label="Способ оплаты">
                      <span className="text-zinc-400">
                        {methodLabels[payment.payment_method]}
                      </span>
                    </td>
                    <td data-label="Статус">
                      <span className={`badge ${status.class} inline-flex items-center gap-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                    </td>
                    <td data-label="Дата" className="text-sm text-zinc-400">
                      {formatDate(payment.created_at)}
                    </td>
                    <td data-label="Действия">
                      <div className="flex items-center justify-end gap-2">
                        {payment.payment_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                          >
                            <a href={payment.payment_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Ссылка
                            </a>
                          </Button>
                        )}
                        {payment.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleConfirm(payment)}
                              data-testid={`confirm-payment-${payment.id}`}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Подтвердить
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancel(payment)}
                              className="text-red-400 hover:text-red-300"
                              data-testid={`cancel-payment-${payment.id}`}
                            >
                              <XCircle className="w-3 h-3 mr-1" />
                              Отменить
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <CreditCard className="empty-state-icon" />
            <p>
              {searchQuery || filterStatus !== 'all' || filterMethod !== 'all'
                ? 'Платежи не найдены'
                : 'Платежей пока нет'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
